<script lang="ts">
    import { computed, reactive, ref } from 'vue';
    import vm from '@journeys/Journeys.viewmodel';
    import { Emittable } from '@shared/utils/emittable';
    import { requestFrame } from '@shared/utils';

    interface ContextContainerState {
        instances: any[];
    }

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'ContextMenu_Container',
    };
    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    const temp_adj_y = 70; // the ungapped header interferes with positioning values...

    const props = withDefaults(
            defineProps<{
                eventController: Emittable;
                margin?: number;
            }>(),
            {
                margin: 24,
            }
        ),
        { viewport } = vm,
        state: ContextContainerState = reactive({
            instances: [],
        }),
        containerRefs = ref<(HTMLElement & ContextContainerState)[]>([]);

    let components: any = {};
    const openMenu = (menu: any, pos: { x: number; y: number }, parentContainer: { h: number; w: number }, dims?: { h: number; w: number }) => {
        if (state.instances.length > 0) return clearAll();

        let instance: any = {
            id: menu._props.data?.id || 'tool',
            parent: parentContainer,
            x: 0,
            y: 0,
            h: 0,
            w: 0,
            head_h: 0,
            head_w: 0,
            body_h: 0,
            body_w: 0,
            foot_h: 0,
            foot_w: 0,
            ref: ref(0),
        };
        components[instance.id] = menu;

        instance.contained = computed(() => {
            let { x, y, h, w, parent } = instance;
            return {
                x: x + w < parent.w ? x : x - (x + w - parent.w) - props.margin,
                y: y + h < parent.h ? y : y - (y + h - parent.h) - props.margin,
                rr: instance.ref.value,
            };
        });

        if (instance.id === 'tool') viewport.contextMenuTargets[instance.id] = instance.id;
        else viewport.contextMenuTargets[components[instance.id]._props.data.id] = components[instance.id]._props.data.id;

        state.instances.push(instance);

        requestFrame(() => {
            for (let el of containerRefs.value) {
                instance.h = el.offsetHeight;
                instance.w = el.offsetWidth;

                for (let ch of el.children as HTMLCollectionOf<HTMLElement>) {
                    instance[`${ch.dataset.el!}_h` as 'x'] = ch.scrollHeight;
                    instance[`${ch.dataset.el!}_w` as 'x'] = ch.scrollWidth;
                }

                instance.x = pos.x - (instance.id === 'tool' ? 0 : instance.w / 2);
                instance.y = pos.y - temp_adj_y - (instance.id === 'tool' ? instance.head_h + 8 : 0); // the 8px is the padding .. .24rem * 2 * 16

                ++instance.ref.value;
            }
        });
    };

    const clearAll = () => {
        for (let { id } of state.instances) closeMenu(id);
    };

    const closeMenu = (id?: number) => {
        !id && clearAll();

        state.instances.find((inst: any, idx: number) => {
            if (inst.id == id) {
                state.instances.splice(idx, 1);
                return true;
            }

            return false;
        });
        delete viewport.contextMenuTargets[id!];
    };

    props.eventController.addListener('opencontextmenu', openMenu);
    props.eventController.addListener('closecontextmenu', closeMenu);
</script>

<template>
    <transition-group name="toggle-context-menu">
        <div
            class="context-menu-container"
            ref="containerRefs"
            v-for="{ id, contained, body_h, body_w } of state.instances"
            :key="id"
            :style="`transform: translate3d(${contained.x}px, ${contained.y}px, 0)`"
            data-el="container">
            <div class="context-menu-head" :class="components[id]._head_color" data-el="head">
                <h3 class="small" v-if="components[id]._head">{{ components[id]._head }}</h3>
            </div>

            <div class="context-menu-body" :class="components[id]._class" :style="`--body-h: ${body_h + 10}px; --body-w: ${body_w}px;`" data-el="body">
                <component :is="components[id]" v-bind="components[id]._props"></component>
            </div>

            <div class="context-menu-foot" :class="components[id]._foot_color" data-el="foot"></div>
        </div>
    </transition-group>
</template>

<style>
    .toggle-context-menu-enter-active,
    .toggle-context-menu-leave-active {
        transition-property: opacity;
        transition-duration: 0.24s;
    }
    .toggle-context-menu-enter-active {
        transition-duration: 0.14s;
        transition-timing-function: ease-in;
    }
    .toggle-context-menu-leave-active {
        transition-delay: 0.08s;
        transition-timing-function: ease-out;
    }

    .toggle-context-menu-enter-active .context-menu-body,
    .toggle-context-menu-leave-active .context-menu-body {
        overflow: hidden;
    }

    .toggle-context-menu-enter-from,
    .toggle-context-menu-leave-to {
        opacity: 0;
    }
    .toggle-context-menu-enter-from .context-menu-body,
    .toggle-context-menu-leave-to .context-menu-body {
        max-height: 0;
    }

    .toggle-context-menu-leave-from,
    .toggle-context-menu-enter-to {
        opacity: 1;
    }
    .toggle-context-menu-leave-from .context-menu-body,
    .toggle-context-menu-enter-to .context-menu-body {
        max-height: var(--body-h);
    }

    .context-menu-container {
        display: flex;
        position: absolute;
        flex-direction: column;

        top: 0;
        left: 0;

        z-index: 10;

        box-shadow: 0.2rem 0.7rem 1.4rem -0.6rem #00000033;
    }

    .context-menu-head {
        flex-shrink: 0;
        min-height: 0.5rem;
        border-bottom: 1px solid #eeeeeec2;
        border-radius: 5px 5px 0 0;
        margin-bottom: -2px;

        background-color: #f8f5f5;
        z-index: 1;
    }

    .context-menu-head h3 {
        color: #006666;
        font-weight: 600;
        text-align: center;
    }

    .context-menu-body {
        padding: 0.25rem;

        background: #fff;
        z-index: 3;

        transform-origin: top;
        transition-property: max-height;
        transition-duration: 0.36s;
        transition-timing-function: var(--ttf-eager);
    }

    .context-menu-foot {
        flex-shrink: 0;
        min-height: 0.5rem;
        border-top: 1px solid #eeeeeec2;
        border-radius: 0 0 5px 5px;
        margin-top: -2px;

        background-color: #f8f5f5;
        z-index: 2;
    }

    .context-menu-head.dark,
    .context-menu-foot.dark {
        background-color: #35323b;
    }

    .context-menu-head.dark h3 {
        color: #dedddf;
    }

    .context-menu-body.minimal {
        padding: 0.24rem;
    }
    .context-menu-head.minimal,
    .context-menu-foot.minimal {
        min-height: 0.24rem;
    }
</style>
